// The creature generator for /sewing-primitives — the curriculum's payoff.
//
// Every dial here is SEMANTIC (chubbiness, head size, limb length...), and
// every one resolves into primitive parameters the lab already teaches: the
// body is a lathe, the head is a gored ball, limbs are cylinders. The load-
// bearing trick is latheRadiusAt: attachment points are computed from the
// body's actual surface, so however you set the dials, the shoulders stay
// on the shoulders. That's what "attachments relative to parent geometry"
// buys — sliders can't produce a floating arm.

import { latheRadiusAt, profileChoices } from "./sewingShapes";
import { buildComposite, deg, transformPoint } from "./sewingComposites";

/* ============================================================
   Dials
   ============================================================ */

export const CREATURE_DIALS = [
  {
    key: "silhouette",
    label: "body silhouette",
    type: "choice",
    def: "egg",
    options: profileChoices(["egg", "pear", "teardrop", "ball", "pin"]),
  },
  { key: "height", label: "body height", min: 8, max: 20, step: 0.5, def: 12, unit: "cm" },
  { key: "chubby", label: "chubbiness", min: 0, max: 1, step: 0.05, def: 0.5, unit: "" },
  { key: "headRatio", label: "head size", min: 0, max: 1, step: 0.05, def: 0.55, unit: "" },
  { key: "limbLength", label: "limb length", min: 0, max: 1, step: 0.05, def: 0.45, unit: "" },
  { key: "earSize", label: "ear size", min: 0, max: 1, step: 0.05, def: 0.5, unit: "" },
  { key: "tail", label: "tail", min: 0, max: 1, step: 0.05, def: 0.35, unit: "" },
  { key: "hue", label: "fur color", min: 0, max: 355, step: 5, def: 200, unit: "°" },
];

export const defaultCreature = () =>
  Object.fromEntries(CREATURE_DIALS.map((d) => [d.key, d.def]));

// Roll random dials. Takes the random source as an argument so tests can
// seed it; the page passes Math.random.
export const randomCreature = (rng) =>
  Object.fromEntries(
    CREATURE_DIALS.map((d) => {
      if (d.type === "choice") {
        const pick = d.options[Math.floor(rng() * d.options.length)];
        return [d.key, pick.value];
      }
      const steps = Math.round((d.max - d.min) / d.step);
      return [d.key, d.min + Math.round(rng() * steps) * d.step];
    })
  );

// A one-line birth certificate, so the dials read back as personality.
export const creatureBlurb = (dials) => {
  const build =
    dials.chubby < 0.33 ? "slender" : dials.chubby < 0.7 ? "comfortable" : "extremely round";
  const legs =
    dials.limbLength < 0.33 ? "stubby" : dials.limbLength < 0.7 ? "sensible" : "gangly";
  const ears = dials.earSize < 0.25 ? "barely-there" : dials.earSize < 0.7 ? "friendly" : "enormous";
  const article = /^[aeiou]/.test(build) ? "an" : "a";
  return `${article} ${build}, ${legs}-limbed ${dials.silhouette}-bodied friend with ${ears} ears`;
};

/* ============================================================
   Dials → parts
   ============================================================ */

// Direction of a cylinder's axis (+z before rotation) after rx/rz.
const axisOf = (rx, rz) => transformPoint([0, 0, 1], { rx: deg(rx), rz: deg(rz) });

export const buildCreature = (dials) => {
  const { silhouette, height, chubby, headRatio, limbLength, earSize, tail, hue } = dials;

  // Semantic dials resolve into primitive measurements (all cm).
  const bodyH = height;
  const bodyW = bodyH * (0.4 + 0.4 * chubby);
  const headR = (bodyW / 2) * (0.55 + 0.9 * headRatio);
  const legLen = bodyH * (0.2 + 0.35 * limbLength);
  const legR = Math.max(0.8, bodyW * (0.09 + 0.05 * chubby));
  const armLen = bodyH * (0.24 + 0.36 * limbLength);
  const armR = Math.max(0.7, bodyW * (0.07 + 0.045 * chubby));
  const earR = headR * (0.22 + 0.5 * earSize);
  const tailR = Math.max(0.6, bodyW * (0.05 + 0.08 * tail));
  const tailLen = bodyH * (0.08 + 0.3 * tail);

  // The body rides on the legs — longer limbs lift the whole creature.
  const lift = legLen * 0.7;

  // Everything below asks the body where its surface actually is.
  const surfaceAt = (frac) =>
    latheRadiusAt(silhouette, bodyW, bodyH, Math.max(bodyH * 0.05, frac * bodyH));

  // Shoulders: embed the arm's top end 25% inside the body's surface.
  const shoulderY = lift + bodyH * 0.6;
  const rShoulder = surfaceAt(0.6);
  const armTilt = 16 + 20 * limbLength; // longer arms flare wider
  const armCenter = (side) => {
    const a = axisOf(75, side * -armTilt); // hangs down and outward
    const s = [side * -rShoulder * 0.75, shoulderY, bodyW * 0.05]; // shoulder end
    return [s[0] + (armLen / 2) * a[0], s[1] + (armLen / 2) * a[1], s[2] + (armLen / 2) * a[2]];
  };

  // Hips: legs stand vertical, tops socketed into the lifted body.
  const rHip = surfaceAt(Math.max(0.06, (legLen - lift) / bodyH));
  const legX = Math.max(rHip * 0.6, legR * 1.15);

  // Neck: the head embeds into the body's top taper.
  const neckY = lift + bodyH * 0.9;
  const headY = neckY + headR * 0.72;

  // Tail: parked on the back surface, tilted up.
  const tailBackR = surfaceAt(0.32);
  const tailY = lift + bodyH * 0.32;

  const FUR = [hue, 52];
  const ACCENT = [(hue + 45) % 360, 62];

  const spec = {
    center: [0, lift + bodyH * 0.55, 0],
    explode: 0.85,
    insight:
      "Every lesson at once: a lathe body, a gored ball head, cylinder limbs. And because the math knows the body's surface, the joints land ON it — no dial setting can make an arm float.",
    parts: [
      {
        id: "body",
        name: "body",
        shape: "lathe",
        params: { profile: silhouette, width: bodyW, height: bodyH, gores: 8, rows: 9 },
        recenter: [0, 0, 0],
        at: [0, lift, 0],
        tone: FUR,
        note: `the ${silhouette} silhouette, spun into gores`,
      },
      {
        id: "head",
        name: "head",
        shape: "sphere",
        params: { radius: headR, gores: 6, rows: 8 },
        recenter: [0, headR, 0],
        at: [0, headY, 0],
        tone: FUR,
        note: "a gored ball, embedded into the body's top taper",
      },
      {
        id: "earL",
        name: "ear",
        shape: "sphere",
        params: { radius: earR, gores: 5, rows: 6 },
        recenter: [0, earR, 0],
        scale: [1, 1, 0.45],
        at: [-headR * 0.6, headY + headR * 0.78, 0.15],
        tone: ACCENT,
        note: "flattened half-balls — only the top half shows",
      },
      {
        id: "earR",
        name: "ear",
        shape: "sphere",
        params: { radius: earR, gores: 5, rows: 6 },
        recenter: [0, earR, 0],
        scale: [1, 1, 0.45],
        at: [headR * 0.6, headY + headR * 0.78, 0.15],
        tone: ACCENT,
      },
      {
        id: "armL",
        name: "arm",
        shape: "cylinder",
        params: { radius: armR, height: armLen, segments: 14 },
        recenter: [0, armR, 0],
        rx: 75,
        rz: -armTilt,
        at: armCenter(1),
        tone: FUR,
        note: "cylinders — the shoulder end embeds in the body",
      },
      {
        id: "armR",
        name: "arm",
        shape: "cylinder",
        params: { radius: armR, height: armLen, segments: 14 },
        recenter: [0, armR, 0],
        rx: 75,
        rz: armTilt,
        at: armCenter(-1),
        tone: FUR,
      },
      {
        id: "legL",
        name: "leg",
        shape: "cylinder",
        params: { radius: legR, height: legLen, segments: 12 },
        recenter: [0, legR, 0],
        rx: 90,
        at: [-legX, legLen / 2, bodyW * 0.04],
        tone: FUR,
        note: "vertical cans — the creature stands on these",
      },
      {
        id: "legR",
        name: "leg",
        shape: "cylinder",
        params: { radius: legR, height: legLen, segments: 12 },
        recenter: [0, legR, 0],
        rx: 90,
        at: [legX, legLen / 2, bodyW * 0.04],
        tone: FUR,
      },
      {
        id: "tail",
        name: "tail",
        shape: "cone",
        params: { radius: tailR, height: tailLen },
        recenter: [0, tailLen / 2, 0],
        rx: -40,
        at: [0, tailY + tailLen * 0.3, -(tailBackR * 0.85 + tailLen * 0.28)],
        tone: ACCENT,
        note: "a little cone, tipped up — swagger is a slider",
      },
    ],
    attachments: [
      { joint: "ladder", a: "head", b: "body", pos: [0, lift + bodyH * 0.93, headR * 0.35], label: "head → body" },
      { joint: "inseam", a: "earL", b: "head", pos: [-headR * 0.55, headY + headR * 0.62, 0.15], label: "ear → head seam" },
      { joint: "inseam", a: "earR", b: "head", pos: [headR * 0.55, headY + headR * 0.62, 0.15], label: "ear → head seam" },
      { joint: "ladder", a: "armL", b: "body", pos: [-rShoulder * 0.95, shoulderY + 0.2, bodyW * 0.12], label: "arm → body" },
      { joint: "ladder", a: "armR", b: "body", pos: [rShoulder * 0.95, shoulderY + 0.2, bodyW * 0.12], label: "arm → body" },
      { joint: "socket", a: "legL", b: "body", pos: [-legX, legLen * 0.95, bodyW * 0.04 + legR * 0.7], label: "leg → body opening" },
      { joint: "socket", a: "legR", b: "body", pos: [legX, legLen * 0.95, bodyW * 0.04 + legR * 0.7], label: "leg → body opening" },
      { joint: "ladder", a: "tail", b: "body", pos: [0, tailY, -tailBackR * 0.95], label: "tail → body" },
    ],
  };

  return buildComposite(spec, 1);
};
